/*
 * Shared by the simple "name → count" report pages (CXO and the panels that reuse them):
 *   CXOPanel/referenceManagerCount.js → referenceTeamLeadCount.js → referenceLeadsCount.js
 * plus a tiny Excel helper any page can use.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { Users } from "lucide-react";
import {
  CustomAdminTable,
  CustomBadge,
  CustomCountChip,
  CustomPageLayout,
  ViewAction,
} from "../../Component/customComponents";
import { BatchNames, TableTitle, buildDateFilterFields, namesText } from "./transactionCommon";

/** rows: array of plain objects (keys = column headers) → downloads "<filename>.xlsx" */
export const exportRowsToExcel = (filename, sheet, rows) => {
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows), sheet);
  XLSX.writeFile(book, `${filename}.xlsx`);
};

const num = (v) => Number(v) || 0;

/** A count you can click to drill down — the shared highlighted chip with a hover tooltip */
export const CountLink = ({ value, onClick, title = "View details", tone }) => (
  <CustomCountChip value={value} onClick={onClick} tooltip={title} tone={tone} />
);

/** Count cell with a small share-of-total bar */
const CountCell = ({ value, total }) => {
  const pct = total ? (num(value) / total) * 100 : 0;
  return (
    <div style={{ minWidth: 120 }}>
      <span className="fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value ? num(value).toLocaleString("en-IN") : "—"}
      </span>
      <span className="text-muted small ms-2">{pct.toFixed(1)}%</span>
      <div className="progress mt-1" style={{ height: 5 }} aria-hidden="true">
        <div className="progress-bar" style={{ width: `${Math.min(100, pct)}%`, background: "var(--cui-primary)" }} />
      </div>
    </div>
  );
};

/** "12.5" / 12.5 → "12.5%"; anything else shown as it comes */
const percentText = (v) =>
  v === null || v === undefined || v === "" ? "—" : Number.isFinite(Number(v)) ? `${Number(v)}%` : String(v);

/** API's own percentage with a small bar (when it's a number) */
const PercentCell = ({ value }) => {
  const n = Number(String(value ?? "").replace("%", ""));
  return (
    <div style={{ minWidth: 110 }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{percentText(value)}</span>
      {Number.isFinite(n) && (
        <div className="progress mt-1" style={{ height: 5 }} aria-hidden="true">
          <div className="progress-bar" style={{ width: `${Math.min(100, n)}%`, background: "var(--cui-primary)" }} />
        </div>
      )}
    </div>
  );
};

const streamName = (row) => row?.name || [row?.examTypeId?.name, namesText(row?.baseCourseId)].filter(Boolean).join(" ");

/**
 * Exam/stream lead counts → state-wise counts for one row (Student Count, Website/App Lead Count).
 *
 * Props:
 *  - pageTitle, tableId
 *  - listTitle    table title before the count, e.g. "Source Leads" → "Source Leads (12)"
 *  - initialFilters, extraFilterFields (e.g. Source, Status)
 *  - fetchList(body, cb, errCb)   cb gets the API response; rows in res.data.Data
 *  - buildListBody(filters)
 *  - fetchStates(body, cb, errCb) cb gets the API response; rows in res.data.data
 *  - buildStateBody(filters, row)
 *  - listExport / stateExport  { filename, sheet } — same names as the old pages
 * Loads once on mount with initialFilters (mount it after any list the defaults depend on).
 */
export const LeadCountStatePage = ({
  pageTitle,
  tableId,
  listTitle = "Leads",
  initialFilters,
  extraFilterFields = [],
  fetchList,
  buildListBody,
  fetchStates,
  buildStateBody,
  listExport,
  stateExport,
}) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [parent, setParent] = useState(null); // the row whose states are open
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);

  const fetchRows = (f) => {
    setLoading(true);
    dispatch(
      fetchList(
        buildListBody(f),
        (res) => {
          const list = Array.isArray(res?.data?.Data) ? [...res.data.Data] : [];
          setRows(list.sort((a, b) => num(b?.studentCount) - num(a?.studentCount)));
          setPage(1);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  const openStates = (row) => {
    setParent(row);
    setStates([]);
    setStatesLoading(true);
    dispatch(
      fetchStates(
        buildStateBody(filters, row),
        (res) => {
          setStates(Array.isArray(res?.data?.data) ? res.data.data : []);
          setStatesLoading(false);
        },
        () => setStatesLoading(false),
      ),
    );
  };

  useEffect(() => {
    fetchRows(filters);
  }, []);

  const total = useMemo(() => rows.reduce((t, r) => t + num(r?.studentCount), 0), [rows]);
  const stateTotal = useMemo(() => states.reduce((t, r) => t + num(r?.studentCount), 0), [states]);

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r?.examTypeId?.name, namesText(r?.baseCourseId), r?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [rows, searchText]);
  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const filterFields = useMemo(
    () => [
      ...buildDateFilterFields().map((field) => ({ ...field, defaultValue: () => initialFilters[field.key] })),
      ...extraFilterFields,
    ],
    [extraFilterFields],
  );

  const countCell = (v) => (
    <span className="fw-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
      {v || v === 0 ? num(v).toLocaleString("en-IN") : "—"}
    </span>
  );

  if (parent) {
    const stateColumns = [
      { key: "name", header: "State Name", minWidth: 180, render: (v) => <strong>{v || "—"}</strong> },
      { key: "studentCount", header: "State Wise Count", align: "right", render: countCell },
      { key: "countPercentage", header: "Percentage", render: (v) => <PercentCell value={v} /> },
    ];
    return (
      <CustomPageLayout title={pageTitle} subtitle={`Parent: ${streamName(parent)}`} isGoBack onBack={() => setParent(null)}>
        <CustomAdminTable
          id={`${tableId}-states`}
          title={<TableTitle title={`States (${states.length})`} from={filters.startDate} to={filters.endDate} />}
          columns={stateColumns}
          data={states}
          loading={statesLoading}
          rowKey={(row, index) => row?._id || `${row?.name}-${index}`}
          enableSerialNumber
          serialHeader="S.No"
          toolbarActions={
            <CustomBadge tone="primary" icon={<Users size={12} />}>
              Total Admission: {stateTotal.toLocaleString("en-IN")}
            </CustomBadge>
          }
          exportButton={states.length > 0}
          onExport={() =>
            exportRowsToExcel(
              stateExport.filename,
              stateExport.sheet,
              states.map((s, i) => ({
                "S.N": i + 1,
                "STATE NAME": s?.name || "-",
                "STATE WISE COUNT": s?.studentCount ?? "-",
                PERCENTAGE: s?.countPercentage ?? "-",
              })),
            )
          }
          emptyTitle="No states"
          emptyDescription="No state wise leads for this exam stream."
          maxHeight="83vh"
        />
      </CustomPageLayout>
    );
  }

  const columns = [
    { key: "exam", header: "Exam Name", minWidth: 150, render: (_, row) => <strong>{row?.examTypeId?.name || "—"}</strong> },
    { key: "stream", header: "Exam Stream", minWidth: 150, render: (_, row) => <BatchNames names={row?.baseCourseId} /> },
    { key: "studentCount", header: "Registration Count", align: "right", render: countCell },
    { key: "countPercentage", header: "Percentage", render: (v) => <PercentCell value={v} /> },
  ];

  return (
    <CustomPageLayout title={pageTitle}>
      <CustomAdminTable
        id={tableId}
        title={<TableTitle title={`${listTitle} (${rows.length})`} from={filters.startDate} to={filters.endDate} />}
        columns={columns}
        data={pagedRows}
        loading={loading}
        rowKey={(row, index) => `${row?.examTypeId?._id}-${row?.baseCourseId?._id}-${index}`}
        enableSerialNumber
        serialHeader="S.No"
        actions={(row) => <ViewAction tooltip="View state wise count" onClick={() => openStates(row)} />}
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search exam or stream...",
        }}
        toolbarActions={
          <CustomBadge tone="primary" icon={<Users size={12} />}>
            Total Leads: {total.toLocaleString("en-IN")}
          </CustomBadge>
        }
        exportButton={rows.length > 0}
        onExport={() =>
          exportRowsToExcel(
            listExport.filename,
            listExport.sheet,
            rows.map((r, i) => ({
              "S.N": i + 1,
              "EXAM NAME": r?.examTypeId?.name || "-",
              "EXAM STREAM": namesText(r?.baseCourseId) || "-",
              "REGISTRATION COUNT": r?.studentCount ?? "-",
              PERCENTAGE: r?.countPercentage ?? "-",
            })),
          )
        }
        advancedFilters={filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          fetchRows(values);
        }}
        applyFiltersOnReset
        filterSubtitle="Pick the date range and filters"
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={searchText ? "No matches" : "No leads"}
        emptyDescription={searchText ? "Try a different search." : "No leads for these filters."}
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};

/**
 * One "name → count" list with a date-range drawer, search, total, export and an optional drill-down.
 *
 * Props:
 *  - pageTitle, context (e.g. "Manager: Asha" — shown as a badge), tableId
 *  - initialFilters   { startDate, endDate, ...extra }
 *  - extraFilterFields  more drawer fields (e.g. Status)
 *  - fetchAction(body, callback, errorCallback)  thunk; callback gets the rows
 *  - buildBody(filters)  request body
 *  - countKey        field holding the count (sorted high → low, as before)
 *  - nameHeader / countHeader   column titles (default "Name" / "Registration Count")
 *  - onDrill(row, filters)      optional: 👁 opens the next level
 *  - exportFile      { filename, sheet } — same file names as the old pages
 *  - isGoBack, onBack           header "Go Back"
 *  - blockedMessage  shown instead of loading when the page can't run (e.g. opened without its parent)
 */
export const CountListPage = ({
  pageTitle,
  context,
  tableId,
  initialFilters,
  extraFilterFields = [],
  fetchAction,
  buildBody,
  countKey,
  nameHeader = "Name",
  countHeader = "Registration Count",
  onDrill,
  drillTooltip = "View details",
  exportFile,
  isGoBack,
  onBack,
  blockedMessage,
}) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchRows = (f) => {
    setLoading(true);
    dispatch(
      fetchAction(
        buildBody(f),
        (res) => {
          const list = Array.isArray(res) ? [...res] : [];
          setRows(list.sort((a, b) => num(b?.[countKey]) - num(a?.[countKey])));
          setPage(1);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    if (!blockedMessage) fetchRows(filters);
  }, []);

  const totalCount = useMemo(() => rows.reduce((t, r) => t + num(r?.[countKey]), 0), [rows, countKey]);

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return term ? rows.filter((r) => String(r?.name || "").toLowerCase().includes(term)) : rows;
  }, [rows, searchText]);
  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  // the date fields reset to this page's own defaults (not always today)
  const filterFields = useMemo(
    () => [
      ...buildDateFilterFields().map((field) => ({ ...field, defaultValue: () => initialFilters[field.key] })),
      ...extraFilterFields,
    ],
    [extraFilterFields],
  );

  const columns = [
    { key: "name", header: nameHeader, minWidth: 200, render: (v) => <strong>{v || "—"}</strong> },
    { key: countKey, header: countHeader, render: (v) => <CountCell value={v} total={totalCount} /> },
  ];

  const onExport = () =>
    exportRowsToExcel(
      exportFile.filename,
      exportFile.sheet,
      rows.map((r, i) => ({ "S.N": i + 1, NAME: r?.name || "-", "REGISTRATION COUNT": r?.[countKey] ?? "-" })),
    );

  return (
    <CustomPageLayout title={pageTitle} isGoBack={isGoBack} onBack={onBack}>
      <CustomAdminTable
        id={tableId}
        title={<TableTitle title={`${nameHeader}s (${rows.length})`} from={filters.startDate} to={filters.endDate} />}
        columns={columns}
        data={pagedRows}
        loading={loading}
        rowKey={(row, index) => row?._id || index}
        enableSerialNumber
        serialHeader="S.No"
        actions={onDrill ? (row) => <ViewAction tooltip={drillTooltip} onClick={() => onDrill(row, filters)} /> : undefined}
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search name...",
        }}
        toolbarActions={
          <>
            {context && <CustomBadge tone="neutral">{context}</CustomBadge>}
            <CustomBadge tone="primary" icon={<Users size={12} />}>
              Total Leads: {totalCount.toLocaleString("en-IN")}
            </CustomBadge>
          </>
        }
        exportButton={rows.length > 0}
        onExport={onExport}
        advancedFilters={blockedMessage ? undefined : filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          fetchRows(values);
        }}
        applyFiltersOnReset
        filterSubtitle="Pick the date range"
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={blockedMessage ? "Nothing to show" : searchText ? "No matching names" : "No leads"}
        emptyDescription={blockedMessage || (searchText ? "Try a different search." : "No leads for this period.")}
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};
